import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  Edit,
  Button,
  FormTab,
  TopToolbar,
  TabbedForm,
  crudGetList,
  useNotify
} from 'react-admin';
import { Grid, Typography } from '@material-ui/core/';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { FormSpy } from 'react-final-form';
import { get, defaultTo, omit } from 'lodash';

import UserPhoneCreateUpdate from './userphone/UserPhoneCreateUpdate';
import UserAddressCreateUpdate from './useraddress/UserAddressCreateUpdate';
import UserAddressDatagrid from './useraddress/UserAddressDatagrid';
import UserPhoneDatagrid from './userphone/UserPhoneDatagrid';
import UserDetails from './UserDetails';
import FormTitleBar from '../../core/components/FormTitleBar';
import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import EditToolbar from '../../core/components/edit/EditToolbar';
import { removePathEnd } from '../../core/utils';
import UserSecurity from './UserSecurity';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import UserAuthorizationTab from './userauthorization/UserAuthorizationTab';
import FeatureAccessControlTab from './featureaccesscontrol/FeatureAccessControlTab';
import Empty from '../../core/components/list/Empty';
import UserApi from '../../core/apis/UserApi';
import UserNotificationTab from './usernotification/UserNotificationTab';
import TenantConfigurationApi from '../../core/apis/TenantConfigurationApi';
import UserActivityTab from './useractivity/UserActivityTab';
import ViewTab from '../../core/components/forked/layout/ViewTab';
import UdfTab from '../../core/components/tab/UdfTab';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import ForceListRender from '../../core/components/forked/list/ForceListRender';

const styles = () => ({
  gridTab: {
    marginTop: '-17px !important'
  },
  noActions: {
    marginTop: '0px'
  }
});

const UserPhoneAddressActionsStyles = {
  addButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    alignSelf: 'center'
  },
  headline: {}
};

const UserAddressActions = withStyles(UserPhoneAddressActionsStyles)(
  ({
    classes,
    handleAddressCreateDialogToggle,
    ids,
    useraddresstypeIds,
    hasEditPermission
  }) => (
    <Grid container justifyContent="space-between" alignItems="center">
      <Typography variant="h5" className={classes.headline}>
        Addresses
      </Typography>
      <TopToolbar>
        {hasEditPermission && ids.length < useraddresstypeIds.length && (
          <Button
            onClick={handleAddressCreateDialogToggle}
            label="Add Address"
            id="add-address"
            variant="outlined"
            color="primary"
            className={classes.addButton}
            disableFocusRipple
          />
        )}
      </TopToolbar>
    </Grid>
  )
);

const UserPhoneActions = withStyles(UserPhoneAddressActionsStyles)(
  ({
    classes,
    handlePhoneCreateDialogToggle,
    ids,
    userphonetypeIds,
    hasEditPermission
  }) => (
    <Grid container justifyContent="space-between" alignItems="center">
      <Typography variant="h5" className={classes.headline}>
        Phone Numbers
      </Typography>
      <TopToolbar>
        {hasEditPermission && ids.length < userphonetypeIds.length && (
          <Button
            onClick={handlePhoneCreateDialogToggle}
            id="add-phone"
            label="Add Phone"
            variant="outlined"
            color="primary"
            className={classes.addButton}
            disableFocusRipple
          />
        )}
      </TopToolbar>
    </Grid>
  )
);

const UserTitle = ({ record, loginUserVisibleFlag }) => {
  const loginUserIdVisible =
    sessionStorage.getItem('isMupChild') === 'true' && loginUserVisibleFlag;
  return (
    <FormTitleBar
      recordLabel={'User ID: ' + record.userId}
      name={record.firstName + ' ' + record.lastName}
      loginUserId={
        loginUserIdVisible ? 'User Login ID: ' + record.mupUserId : null
      }
      status={record.status}
      statusLabel={'Status:'}
      showAvatar={true}
      avatarContent={
        (record.firstName
          ? record.firstName.substring(0, 1).toUpperCase()
          : '') +
        (record.lastName ? record.lastName.substring(0, 1).toUpperCase() : '')
      }
    />
  );
};

const UserEdit = props => {
  const {
    crudGetList,
    classes,
    useraddresstypeIds,
    userphonetypeIds,
    userType,
    permissions = [],
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    hasReadPermission,
    documentTypes,
    userProfile = {},
    match,
    ...rest
  } = props;
  const [showPhoneCreateDialog, setShowPhoneCreateDialog] = useState(false);
  const [showPhoneEditDialog, setShowPhoneEditDialog] = useState(false);
  const [showAddressCreateDialog, setShowAddressCreateDialog] = useState(false);
  const [showAddressEditDialog, setShowAddressEditDialog] = useState(false);
  const [phoneRecord, setPhoneRecord] = useState({});
  const [phoneBasePath, setPhoneBasePath] = useState('');
  const [addressRecord, setAddressRecord] = useState({});
  const [addressBasePath, setAddressBasePath] = useState('');
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);
  const [loginUserIdVisible, setLoginUserIdVisible] = useState(false);
  const [notificationTabKey, setNotificationTabKey] = useState(0);

  const notify = useNotify();
  const notifyServerError = useNotifyServerError();

  const checkShowUserLoginIDConfiguration = useCallback(() => {
    TenantConfigurationApi.get('ShowUserLoginID').then(info => {
      const value =
        info &&
        info.pageData &&
        info.pageData.length > 0 &&
        info.pageData[0].value &&
        info.pageData[0].value === '1';
      setLoginUserIdVisible(value);
    });
  }, []);

  const handlePhoneCreateDialogToggle = useCallback(
    () => setShowPhoneCreateDialog(prevState => !prevState),
    []
  );

  const handlePhoneEditButtonClick = useCallback(
    (phoneRecord, phoneBasePath) => {
      setShowPhoneEditDialog(prevState => !prevState);
      setPhoneRecord(phoneRecord);
      setPhoneBasePath(phoneBasePath);
    },
    []
  );

  const handleAddressCreateDialogToggle = useCallback(
    () => setShowAddressCreateDialog(prevState => !prevState),
    []
  );

  const handleAddressEditButtonClick = useCallback(
    (addressRecord, addressBasePath) => {
      setShowAddressEditDialog(prevState => !prevState);
      setAddressRecord(addressRecord);
      setAddressBasePath(addressBasePath);
    },
    []
  );

  const handleResendWelcomeEmail = useCallback(
    (userId, formProps) => {
      UserApi.sendWelcomeEmail(userId)
        .then(() => {
          formProps.form.change('welcomeMailHasBeenSent', true);
          notify('Welcome email sent');
        })
        .catch(notifyServerError);
    },
    [notify, notifyServerError]
  );

  const onBeforeDelete = useCallback(() => setWhenBlockNavigation(false), []);

  useEffect(() => {
    // Retrieve the list of user phone and address types (only displayed types)
    crudGetList(
      'userphonetypes',
      { page: 1, perPage: -1 },
      {},
      { display: true }
    );
    crudGetList(
      'useraddresstypes',
      { page: 1, perPage: -1 },
      {},
      { display: true }
    );
    crudGetList('documenttypes', { page: 1, perPage: -1 });
    checkShowUserLoginIDConfiguration();
  }, [crudGetList, checkShowUserLoginIDConfiguration]);

  const displayToolbar = match.isExact;
  const [
    hasReadUserAuthorizationPermission,
    hasReadUserRolePermission,
    hasComposeEmailPermission,
    hasReadActivityPermission,
    hasUserWelcomeEmailPermission
  ] = useMemo(
    () => [
      permissions.includes('AUTH_READ_USER_AUTHORIZATION_NA'),
      permissions.includes('AUTH_READ_USER_ROLE'),
      permissions.includes('AUTH_COMPOSE_EMAIL'),
      permissions.includes('AUTH_READ_USER_ACTIVITIES'),
      permissions.includes('AUTH_EDIT_USER_WELCOME_EMAIL_FLAG')
    ],
    [permissions]
  );

  const handleNotificationTabClick = () => {
    setNotificationTabKey(notificationTabKey + 1);
  };

  return (
    <React.Fragment>
      <NavigationPrompt
        when={whenBlockNavigation}
        adjustMatchingPath={removePathEnd}
        hasEditPermission={hasEditPermission}
        hasComposeEmailPermission={hasComposeEmailPermission}
        isMup={sessionStorage.getItem('isMupChild') === 'true'}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onConfirm={onConfirm}
            onClose={onCancel}
          />
        )}
      </NavigationPrompt>
      <Edit
        {...rest}
        undoable={false}
        title={<UserTitle loginUserVisibleFlag={loginUserIdVisible} />}
        classes={{ noActions: classes.noActions }}
      >
        <TabbedForm
          toolbar={
            displayToolbar ? (
              <EditToolbar
                {...rest}
                hasEditPermission={hasEditPermission}
                hasDeletePermission={hasDeletePermission}
                onBeforeDelete={onBeforeDelete}
              />
            ) : (
              <></>
            )
          }
          variant="standard"
          margin="normal"
        >
          <FormTab label="Contact Details">
            <UserDetails
              creation={false}
              handleResend={handleResendWelcomeEmail}
              hasComposeEmailPermission={hasComposeEmailPermission}
              hasUserWelcomeEmailPermission={hasUserWelcomeEmailPermission}
              hasEditPermission={hasEditPermission}
              userProfile={userProfile}
            />
            <FormSpy
              subscription={{ dirty: true }}
              onChange={props => {
                // this was called on render and makes React unhappy (before timeout)
                setTimeout(() => setWhenBlockNavigation(props.dirty));
              }}
            />
          </FormTab>
          <ViewTab label="Phone Numbers">
            <ForceListRender
              style={{ margin: '0px', width: '100%' }}
              pagination={null}
              perPage={-1}
              basePath="/userphones"
              resource="userphones"
              filter={{ userId: rest.id }}
              className={classes.gridTab}
              sort={{ field: 'phoneNumber', order: 'ASC' }}
              title=" "
              actions={
                <UserPhoneActions
                  handlePhoneCreateDialogToggle={handlePhoneCreateDialogToggle}
                  userphonetypeIds={userphonetypeIds}
                  hasEditPermission={hasEditPermission}
                />
              }
              bulkActionButtons={
                hasEditPermission && <BulkActionDeleteButton />
              }
              empty={
                <Empty
                  emptyText="There are no Phone Numbers to be displayed"
                  title="Phone Numbers"
                  buttons={[
                    {
                      label: 'Add Phone',
                      onClick: handlePhoneCreateDialogToggle,
                      hide: !hasEditPermission
                    }
                  ]}
                />
              }
            >
              <UserPhoneDatagrid
                handlePhoneEditButtonClick={handlePhoneEditButtonClick}
                hasEditPermission={hasEditPermission}
              />
            </ForceListRender>
            {showPhoneEditDialog && (
              <UserPhoneCreateUpdate
                operation="Update"
                title=" "
                popupTitle="Edit Phone"
                okButtonLabel="Save"
                handleDialogToggle={setShowPhoneEditDialog}
                {...rest}
                userPhoneId={phoneRecord.id}
                basePath={phoneBasePath}
                resource="userphones"
              />
            )}
            {showPhoneCreateDialog && (
              <UserPhoneCreateUpdate
                operation="Create"
                title=" "
                popupTitle="Add Phone"
                okButtonLabel="Add"
                handleDialogToggle={handlePhoneCreateDialogToggle}
                {...rest}
              />
            )}
          </ViewTab>
          <ViewTab label="Addresses">
            <ForceListRender
              style={{ margin: '0px', width: '100%' }}
              pagination={null}
              perPage={-1}
              basePath="/useraddresses"
              resource="useraddresses"
              className={classes.gridTab}
              filter={{ userId: rest.id }}
              sort={{ field: 'address', order: 'ASC' }}
              title=" "
              actions={
                <UserAddressActions
                  handleAddressCreateDialogToggle={
                    handleAddressCreateDialogToggle
                  }
                  useraddresstypeIds={useraddresstypeIds}
                  hasEditPermission={hasEditPermission}
                />
              }
              bulkActionButtons={
                hasEditPermission && <BulkActionDeleteButton />
              }
              empty={
                <Empty
                  emptyText="There are no Addresses to be displayed"
                  title="Addresses"
                  buttons={[
                    {
                      label: 'Add Address',
                      onClick: handleAddressCreateDialogToggle,
                      hide: !hasEditPermission
                    }
                  ]}
                />
              }
            >
              <UserAddressDatagrid
                handleAddressEditButtonClick={handleAddressEditButtonClick}
                hasEditPermission={hasEditPermission}
              />
            </ForceListRender>
            {showAddressEditDialog && (
              <UserAddressCreateUpdate
                operation="Update"
                title=" "
                popupTitle="Edit Address"
                okButtonLabel="Save"
                handleDialogToggle={setShowAddressEditDialog}
                {...rest}
                addressId={addressRecord.id}
                basePath={addressBasePath}
                resource="useraddresses"
              />
            )}
            {showAddressCreateDialog && (
              <UserAddressCreateUpdate
                operation="Create"
                title=" "
                popupTitle="Add Address"
                okButtonLabel="Add"
                handleDialogToggle={handleAddressCreateDialogToggle}
                {...rest}
              />
            )}
          </ViewTab>
          {userProfile.userType !== 'INVESTOR_ADMIN' && (
            <FormTab label="Security">
              <UserSecurity
                permissions={permissions}
                {...rest}
                userProfile={userProfile}
              />
            </FormTab>
          )}
          <ViewTab
            label="Notifications"
            onClick={() => {
              handleNotificationTabClick();
            }}
          >
            <UserNotificationTab
              {...omit(props, ['match', 'location'])}
              permissions={permissions}
              notificationTabKey={notificationTabKey}
            />
          </ViewTab>
          {(userType === 'FUND_ADMIN' || userType === 'INVESTOR_ADMIN') &&
            hasReadUserRolePermission && (
              <ViewTab label="Access Roles">
                <FeatureAccessControlTab
                  {...rest}
                  userType={userType}
                  classes={classes}
                  hasDeletePermission={hasDeletePermission}
                  permissions={permissions}
                />
              </ViewTab>
            )}
          {userType !== 'SUPER_ADMIN' && hasReadUserAuthorizationPermission && (
            <ViewTab label="Access Rights">
              <UserAuthorizationTab
                {...rest}
                userType={userType}
                onChangeAuthorizations={setWhenBlockNavigation}
                permissions={permissions}
                userProfile={userProfile}
                whenBlockNavigation={whenBlockNavigation}
              />
            </ViewTab>
          )}
          {hasReadActivityPermission && (
            <ViewTab label="Activities">
              <UserActivityTab userId={rest.id} permissions={permissions} />
            </ViewTab>
          )}
          <ViewTab label="Properties">
            <UdfTab
              id={rest.id}
              queryId="userId"
              type="user"
              hasEditPermission={hasEditPermission}
              onChangeUDFs={setWhenBlockNavigation}
              {...rest}
            />
          </ViewTab>
        </TabbedForm>
      </Edit>
    </React.Fragment>
  );
};

const mapStateToProps = (state, props) => {
  const user = state.admin.resources.users
    ? state.admin.resources.users.data[props.id]
    : null;
  const userType = get(user, 'userType', '');

  return {
    userphonetypeIds: state.admin.resources.userphonetypes
      ? state.admin.resources.userphonetypes.list.ids
      : [], // List of user phone types
    useraddresstypeIds: state.admin.resources.useraddresstypes
      ? state.admin.resources.useraddresstypes.list.ids
      : [], // List of user address types
    userType,
    documentTypes: defaultTo(
      get(state, 'admin.resources.documenttypes.data'),
      []
    ),
    useraddresstypes: defaultTo(
      get(state, 'admin.resources.useraddresstypes.data'),
      []
    )
  };
};

const mapDispatchToProps = {
  crudGetList
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(UserEdit));
